import { Table, TableWrapper, Tbody, Td, Tr } from '@components/core-ui/Table';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import TrashBin from '@components/icons/TrashBin.svg';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RepositoriesWithStatusesResponseDto, RepositoryWithStatusesDto } from 'app/API';
import { createFetchOrganizationRepositoriesQueryFn } from 'app/queries/repositories/createFetchOrganizationRepositoriesQueryFn';
import { repositoriesKeys, TRepositoriesListParams } from 'app/queries/repositories/repositoriesKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadError } from '../LoadError';
import { CellContent } from './styles/CellContent';
import { LastCellContent } from './styles/LastCellContent';
import { Wrapper } from './styles/Wrapper';

const EMPTY_REPOSITORIES: RepositoryWithStatusesDto[] = [];
const ALL_REPOSITORIES_PARAMS: TRepositoriesListParams = { page: 1, pageSize: 10000, search: '', from: '', to: '' };

export const RepositoriesTable = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { openModal, closeModal } = useModalContext();
  const { organization } = useAuthContext();

  const orgId = organization?.id ?? '';
  const [repositoriesDisclosureOpen, setRepositoriesDisclosureOpen] = useState<boolean>(true);

  const repositoriesListKey = repositoriesKeys.list(orgId, ALL_REPOSITORIES_PARAMS);
  const repositoriesQuery = useQuery({
    queryKey: repositoriesListKey,
    queryFn: createFetchOrganizationRepositoriesQueryFn(call),
    enabled: orgId !== '',
  });
  const repositories = repositoriesQuery.data?.data ?? EMPTY_REPOSITORIES;

  const repositoriesSectionTitle = `Cases ${repositories.length > 0 ? ` (${repositories.length})` : ''}`;

  const handleDeleteRepository = (repository: RepositoryWithStatusesDto) => {
    openModal(ModalNamesEnum.DeleteRepositoryModal, {
      repository,
      onSuccess: () => {
        closeModal();
        queryClient.setQueryData<RepositoriesWithStatusesResponseDto>(repositoriesListKey, (current) =>
          current ? { ...current, data: current.data.filter((r) => r.id !== repository.id) } : current,
        );
        void queryClient.invalidateQueries({ queryKey: repositoriesListKey });
      },
    });
  };

  return (
    <Wrapper className="flex flex-col">
      <div>
        <DisclosureSection
          title={repositoriesSectionTitle}
          open={repositoriesDisclosureOpen}
          setOpenState={setRepositoriesDisclosureOpen}
          loading={false}
        >
          {repositoriesQuery.isError ? (
            <LoadError message="Failed to load cases." onRetry={() => void repositoriesQuery.refetch()} />
          ) : null}
          <TableWrapper>
            <Table>
              <Tbody>
                {repositories.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent style={{ width: '250px' }}>
                        <Link
                          to={{
                            pathname: routes.repositoryDetails.make(organization?.slug, data.slug, data.id),
                          }}
                          state={{
                            from: routes.organizationDetails.make(organization?.slug),
                          }}
                        >
                          {data.name}
                        </Link>
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <span style={{ display: 'block' }}>{data.jobsProcessing} jobs processing</span>
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <span style={{ display: 'block' }}>{data.jobsReadyForTranscribing} jobs ready for editing</span>
                      </CellContent>
                    </Td>
                    <Td style={{ width: '1%' }}>
                      <CellContent>
                        <span style={{ display: 'block' }}>{data.jobsComplete} completed jobs</span>
                      </CellContent>
                    </Td>
                    <Td style={{ width: '1%' }}>
                      <LastCellContent>
                        <TrashBin
                          className="hover:text-[var(--color-title)] text-[var(--color-text-muted)] cursor-pointer"
                          onClick={() => handleDeleteRepository(data)}
                        />
                      </LastCellContent>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
          <div className="grid place-items-end">
            <div className="w-36">
              <OutlinedButton
                className="my-2 w-[140px]"
                disabled={false}
                onClick={() =>
                  navigate(
                    {
                      pathname: routes.newRepository.make(organization?.slug),
                    },
                    {
                      state: {
                        from: routes.organizationDetails.make(organization?.slug),
                      },
                    },
                  )
                }
              >
                Create a new Case
              </OutlinedButton>
            </div>
          </div>
        </DisclosureSection>
      </div>
    </Wrapper>
  );
};
